import React, { useContext, useState } from 'react';
import { IoIosArrowForward } from "react-icons/io";
import { MdOutlineSensors, MdOutlineMedicalServices } from "react-icons/md";
import {
  LuClock,
  LuHourglass,
  LuMapPin,
  LuVideo,
  LuRadio,
} from "react-icons/lu";
import { Spinner } from "reactstrap";
import { useNavigate } from 'react-router-dom';
import { Language, PostDetailContext } from '../../../App';
import { _imagesURL } from '../../../constants/API';
import { Zoom3DIcon } from "../../agenda/common/Agenda3DIcons";
import { getSidebarFormattedDate } from "../../../utils/agendaUtils";
import { LIVE_WEBINARS } from '../../../constants/RoutesUrl';
import defaultAgenda1 from "../../../assets/images/default_agenda_1.jpg";
import "../../agenda/AgendaUpcomingSidebar.css";
import "../../agenda/AgendaFoundation.css";

/* ── Thumbnail Image Loader (Matching AgendaUpcomingSidebar) ── */
function FeedThumbImage({ src, alt, defaultSrc }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className="agenda-timeline-thumb-wrap d-flex align-items-center justify-content-center"
      style={{
        position: "relative",
        background: "#F4F7FB",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: loaded ? 0 : 1,
          transition: "opacity 0.35s ease-out",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <Spinner
          style={{
            color: "#1dc1dd",
            width: "8px",
            height: "8px",
            borderWidth: "2px",
          }}
        />
      </div>
      <img
        src={src || defaultSrc}
        alt={alt || "Feed"}
        className="agenda-timeline-thumb-img"
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = defaultSrc;
          setLoaded(true);
        }}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: loaded ? 1 : 0,
          transition:
            "opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
          willChange: "opacity, transform",
        }}
      />
    </div>
  );
}

/* ── Modern Sidebar Shimmer Item (Matching AgendaUpcomingSidebarItemShimmer) ── */
const LiveFeedShimmerRow = ({ titleWidth = "70%", hasCountdown = true, locWidth = "32%", typeWidth = "28%" }) => (
  <div className="agenda-sidebar-shimmer-row" aria-hidden="true">
    {/* Thumbnail */}
    <div className="agenda-sidebar-shimmer-thumb agenda-shimmer-block" />

    {/* Info Column - 3 Lines */}
    <div className="agenda-sidebar-shimmer-info">
      {/* Line 1: Title + Countdown */}
      <div className="d-flex align-items-center justify-content-between gap-2 w-100">
        <span
          className="agenda-sidebar-shimmer-title agenda-shimmer-block"
          style={{ width: titleWidth }}
        />
        {hasCountdown && (
          <span
            className="agenda-sidebar-shimmer-countdown agenda-shimmer-block"
            style={{ width: "36px", height: "18px", borderRadius: "5px", flexShrink: 0 }}
          />
        )}
      </div>

      {/* Line 2: Time */}
      <div className="d-flex align-items-center justify-content-between gap-2 w-100">
        <span
          className="agenda-sidebar-shimmer-meta agenda-shimmer-block"
          style={{ width: "45%" }}
        />
      </div>

      {/* Line 3: Badges */}
      <div className="d-flex align-items-center justify-content-between gap-2 w-100">
        <span
          className="agenda-sidebar-shimmer-badge agenda-shimmer-block"
          style={{ width: locWidth, height: "18px", borderRadius: "5px", flexShrink: 0 }}
        />
        <span
          className="agenda-sidebar-shimmer-badge agenda-shimmer-block"
          style={{ width: typeWidth, height: "18px", borderRadius: "5px", flexShrink: 0 }}
        />
      </div>
    </div>
  </div>
);

/* ── Full Sidebar Shimmer (Matching AgendaUpcomingSidebarShimmer) ── */
const LiveFeedShimmer = () => (
  <div className="agenda-sidebar-shimmer-container" role="status" aria-label="Loading live feeds">
    <div className="agenda-sidebar-shimmer-group">
      <span
        className="agenda-sidebar-shimmer-group-label agenda-shimmer-block"
        style={{ width: "115px", height: "14px", borderRadius: "4px" }}
      />
      <div className="agenda-timeline-items">
        <LiveFeedShimmerRow titleWidth="72%" hasCountdown={true} locWidth="32%" typeWidth="28%" />
        <LiveFeedShimmerRow titleWidth="65%" hasCountdown={false} locWidth="28%" typeWidth="26%" />
      </div>
    </div>

    <div className="agenda-sidebar-shimmer-group">
      <span
        className="agenda-sidebar-shimmer-group-label agenda-shimmer-block"
        style={{ width: "135px", height: "14px", borderRadius: "4px" }}
      />
      <div className="agenda-timeline-items">
        <LiveFeedShimmerRow titleWidth="76%" hasCountdown={true} locWidth="34%" typeWidth="30%" />
      </div>
    </div>
  </div>
);

export default function LiveFeeds({ leftSideFeeds, isLoading }) {
  const { lang } = useContext(Language);
  const navigate = useNavigate();

  const categoriesConfig = {
    [lang.live_webinars]: {
      id: 1,
      name: lang.live_webinars,
      color: "#00BADA",
      badgeBg: "#E0F7FA",
      type: 'webinar',
      isLive: true,
    },
    [lang.live_clinical_sessions]: {
      id: 2,
      name: lang.live_clinical_sessions,
      color: "#EF4444",
      badgeBg: "#FEF2F2",
      type: 'clinical-session',
      isLive: true,
    },
    [lang.upcoming_webinars]: {
      id: 3,
      name: lang?.upcoming_webinars,
      color: "#F59E0B",
      badgeBg: "#FEF3C7",
      type: 'webinar',
      isLive: false,
    },
    [lang.upcoming_slinical_sessions]: {
      id: 4,
      name: lang.upcoming_clinical_sessions,
      color: "#6366F1",
      badgeBg: "#EEF2FF",
      type: 'clinical-session',
      isLive: false,
    },
  };

  const { live, upcoming } = leftSideFeeds || {};
  const initialLiveWebinars = live?.webinar?.length ?? 0;
  const initialLiveClinical = live?.['clinical-session']?.length ?? 0;
  const initialUpcomingWebinars = upcoming?.webinar?.length ?? 0;
  const initialUpcomingClinical = upcoming?.['clinical-session']?.length ?? 0;

  let liveWebinars = Math.min(initialLiveWebinars, 3);
  let liveClinical = Math.min(initialLiveClinical, 3);
  let totalLive = liveWebinars + liveClinical;
  let spaceRemaining = 6 - totalLive;

  const _upcomingWebinars =
    initialUpcomingClinical >= 3
      ? 3
      : initialUpcomingClinical <= 1
        ? 4
        : initialUpcomingClinical < 2
          ? 3
          : 4;

  let upcomingWebinars = Math.min(initialUpcomingWebinars, Math.min(_upcomingWebinars, spaceRemaining));
  let upcomingClinical = Math.min(initialUpcomingClinical, Math.max(0, spaceRemaining - upcomingWebinars));

  let sections = [
    { data: live?.webinar?.slice(0, liveWebinars), type: lang.live_webinars },
    { data: live?.['clinical-session']?.slice(0, liveClinical), type: lang.live_clinical_sessions },
    { data: upcoming?.webinar?.slice(0, upcomingWebinars), type: lang.upcoming_webinars },
    { data: upcoming?.['clinical-session']?.slice(0, upcomingClinical), type: lang.upcoming_slinical_sessions }
  ].filter(section => section?.data?.length > 0);

  // If not loading and no content, don't show the card
  if (!isLoading && sections.length === 0) {
    return null;
  }

  return (
    <div
      className="bg-white d-xl-block d-none mb-3 position-relative overflow-hidden"
      style={{
        width: "335px",
        maxWidth: "335px",
        borderRadius: "24px",
        border: "1px solid #D7E0EA",
        background: "linear-gradient(180deg, #FFFFFF 0%, #F8FDFF 100%)",
        boxShadow: "0px 8px 30px rgba(0, 186, 218, 0.05), 0px 2px 8px rgba(0, 0, 0, 0.03)",
        overflowY: "auto",
        transition: "all 0.25s ease",
        fontFamily: "'Onest', sans-serif",
      }}
    >
      {/* Ambient Glow */}
      <div
        style={{
          position: "absolute",
          top: "-30px",
          right: "-30px",
          width: "120px",
          height: "120px",
          background: "#00BADA",
          opacity: 0.1,
          filter: "blur(45px)",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Header Bar */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center position-relative"
        style={{ borderColor: "#F1F5F9", zIndex: 2 }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: "28px",
              height: "28px",
              background: "#E8FBFF",
              color: "#00BADA",
              border: "1px solid #BCEEFA",
            }}
          >
            <MdOutlineSensors size={16} />
          </div>
          <h4 className="fs-15 roboto-medium mb-0" style={{ color: '#0F1A2B', lineHeight: 1.2, fontWeight: 600 }}>
            {lang.live_feeds || "Live Feeds"}
          </h4>
        </div>
        <span
          onClick={() => navigate(LIVE_WEBINARS)}
          className="fs-12 cursor roboto-medium mb-0 hover-blue d-inline-flex align-items-center gap-1"
          style={{ color: "#00BADA", transition: "all 0.2s ease", fontWeight: 600 }}
        >
          {lang.view_all || "View All"} <IoIosArrowForward size={13} />
        </span>
      </div>

      {/* Internal Timeline Content Area (Matching AgendaUpcomingSidebar) */}
      <div className="position-relative" style={{ zIndex: 2, padding: "12px 10px 14px" }}>
        {isLoading ? (
          <LiveFeedShimmer />
        ) : (
          <div className="d-flex flex-column" style={{ gap: "14px" }}>
            {sections.map((section, index) => {
              const cfg = categoriesConfig[section.type] || {};
              return (
                <CategorySection
                  key={index}
                  title={section.type}
                  items={section.data}
                  color={cfg.color || "#00BADA"}
                  isLive={cfg.isLive}
                  categoryType={cfg.type}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const CategorySection = ({ title, items, color, isLive, categoryType }) => {
  return (
    <div className="agenda-timeline-group">
      {/* Date / Category Sub-Header */}
      <div className="agenda-timeline-date-header">
        <div
          className="agenda-timeline-date-node"
          style={{
            background: color,
            boxShadow: isLive ? `0 0 6px ${color}` : "none",
          }}
        />
        <span
          className="agenda-timeline-date-title"
          style={{ color: isLive ? color : "#64748B" }}
        >
          {title}
        </span>
        <div className="agenda-timeline-date-line" />
      </div>

      {/* Timeline Item Rows Container */}
      <div className="agenda-timeline-items">
        {items.map((item) => (
          <LiveSectionCard
            key={item.id || item.post_id || item.session_id}
            item={item}
            categoryType={categoryType}
            isSectionLive={isLive}
          />
        ))}
      </div>
    </div>
  );
};

const resolveItemThumb = (item) => {
  let raw = "";
  if (item?.type === "webinar") {
    if (Array.isArray(item?.media) && item.media.length > 0 && item.media[0]) {
      raw = item.media[0];
    } else if (typeof item?.media === "string" && item.media) {
      raw = item.media;
    } else if (item?.image) {
      raw = item.image;
    } else if (item?.user?.image) {
      raw = item.user.image;
    }
  } else {
    if (item?.image) {
      raw = item.image;
    } else if (Array.isArray(item?.media) && item.media.length > 0 && item.media[0]) {
      raw = item.media[0];
    } else if (typeof item?.media === "string" && item.media) {
      raw = item.media;
    } else if (item?.user?.image) {
      raw = item.user.image;
    }
  }
  if (!raw) return defaultAgenda1;
  if (typeof raw !== "string") return defaultAgenda1;
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") return defaultAgenda1;
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:") ||
    trimmed.includes("assets/")
  ) {
    return trimmed;
  }
  const base = _imagesURL.trim().replace(/\/+$/, "");
  const path = trimmed.replace(/^\/+/, "");
  return `${base}/${path}`;
};

const formatRelativeTime = (timeDiff, lang) => {
  const minutes = Math.abs(Math.floor(timeDiff / 60));
  const hours = Math.abs(Math.floor(minutes / 60));
  const days = Math.abs(Math.floor(hours / 24));
  const months = Math.abs(Math.floor(days / 30));
  const years = Math.abs(Math.floor(days / 365));

  if (timeDiff > 0) {
    if (minutes === 0) return `${lang?.starts_in || "Starts in"} ${lang?.less_than_a_minute || "< 1 min"}`;
    if (minutes < 60) return `${lang?.starts_in || "Starts in"} ${minutes} ${minutes === 1 ? (lang?.minute || "min") : (lang?.minutes || "mins")}`;
    if (hours < 24) return `${lang?.starts_in || "Starts in"} ${hours} ${hours === 1 ? (lang?.hour || "hr") : (lang?.hours || "hrs")}`;
    if (days < 30) return `${lang?.starts_in || "Starts in"} ${days} ${days === 1 ? (lang?.day || "day") : (lang?.days || "days")}`;
    if (months < 12) return `${lang?.starts_in || "Starts in"} ${months} ${months === 1 ? (lang?.month || "month") : (lang?.months || "months")}`;
    return `${lang?.starts_in || "Starts in"} ${years} ${years === 1 ? (lang?.year || "year") : (lang?.years || "years")}`;
  } else {
    if (minutes < 60) return `${lang?.started || "Started"} ${minutes} ${minutes === 1 ? (lang?.minute || "min") : (lang?.minutes || "mins")} ${lang?.ago || "ago"}`;
    if (hours < 24) return `${lang?.started || "Started"} ${hours} ${hours === 1 ? (lang?.hour || "hr") : (lang?.hours || "hrs")} ${lang?.ago || "ago"}`;
    if (days < 30) return `${lang?.started || "Started"} ${days} ${days === 1 ? (lang?.day || "day") : (lang?.days || "days")} ${lang?.ago || "ago"}`;
    if (months < 12) return `${lang?.started || "Started"} ${months} ${months === 1 ? (lang?.month || "month") : (lang?.months || "months")} ${lang?.ago || "ago"}`;
    return `${lang?.started || "Started"} ${years} ${years === 1 ? (lang?.year || "year") : (lang?.years || "years")} ${lang?.ago || "ago"}`;
  }
};

const formatShortPillCountdown = (timeDiff) => {
  if (!timeDiff || timeDiff <= 0) return "Started";
  const minutes = Math.floor(timeDiff / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) {
    const remMins = minutes % 60;
    return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
  }
  if (days < 7) {
    const remHours = hours % 24;
    return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
  }
  return `${days}d`;
};

const LiveSectionCard = ({ item, categoryType, isSectionLive }) => {
  const { lang } = useContext(Language);
  const navigate = useNavigate();

  const currentTime = Math.floor(Date.now() / 1000);
  const startTimestamp = parseInt(item.start_timestamp || item.timestamp, 10);
  const timeDifference = !isNaN(startTimestamp) && startTimestamp > 0 ? startTimestamp - currentTime : 0;

  const isLive = isSectionLive || item.is_live || item.status === "started" || item.status === "live" || timeDifference < 0;
  const formattedRelativeTime = formatRelativeTime(timeDifference, lang);
  const shortCountdown = formatShortPillCountdown(timeDifference);

  const dateTimeDisplay = item.date
    ? getSidebarFormattedDate(item.date, item.end_date, item.time || item.start_time)
    : !isNaN(startTimestamp) && startTimestamp > 0
      ? new Date(startTimestamp * 1000).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : formattedRelativeTime;

  const thumbSrc = resolveItemThumb(item);
  const isWebinar = categoryType === "webinar" || item.type === "webinar";

  const onClickFeedCard = () => {
    const targetId = item.id || item.post_id || item.session_id;
    if (isWebinar) {
      navigate(`/webinar/${item.seo_slug || targetId}`, { state: { webinarData: item } });
    } else {
      navigate(`/session/${targetId}`, { state: { sessionData: item } });
    }
  };

  return (
    <div
      onClick={onClickFeedCard}
      className="agenda-timeline-row"
      role="button"
      tabIndex={0}
    >
      {/* Thumbnail Wrap with Shimmer Loading */}
      <FeedThumbImage
        src={thumbSrc}
        alt={item.title}
        defaultSrc={defaultAgenda1}
      />

      {/* Main Content Area: Exactly 3 balanced lines */}
      <div className="agenda-timeline-content">
        {/* Line 1: Agenda Title & (Countdown/Status Badge opposite of title) */}
        <div className="agenda-timeline-title-row">
          <h6 className="agenda-timeline-title" title={item.title}>
            {item.title}
          </h6>

          <div className="agenda-timeline-tags-group">
            <span
              className={`agenda-pill-countdown ${isLive ? "is-live" : ""}`}
              title={isLive ? (lang?.live_now || "Live Now") : formattedRelativeTime}
            >
              {isLive ? (
                <LuRadio size={10} />
              ) : (
                <LuHourglass size={10} />
              )}
              <span>{isLive ? (lang?.live || "LIVE") : shortCountdown}</span>
            </span>
          </div>
        </div>

        {/* Line 2: Time / Date */}
        <div className="agenda-timeline-time-row">
          <span className="agenda-meta-time">
            <LuClock size={11} className="agenda-meta-icon" />
            <span>{dateTimeDisplay}</span>
          </span>
        </div>

        {/* Line 3: Mode Tag (Online / Offline / Hybrid), Physical Location & Category Pill */}
        <div className="agenda-timeline-host-row">
          <div className="agenda-timeline-badges-left">
            {item.is_hybrid || item.isHybrid ? (
              <span className="agenda-location-badge is-hybrid" title="Hybrid">
                <LuRadio size={10} />
                <span>Hybrid</span>
              </span>
            ) : item.is_online !== false ? (
              <>
                <span className="agenda-location-badge is-online" title="Online">
                  <LuVideo size={10} />
                  <span>Online</span>
                </span>
                <Zoom3DIcon height={18} />
              </>
            ) : (
              <span className="agenda-location-badge is-location" title="Offline">
                <LuMapPin size={10} />
                <span>Offline</span>
              </span>
            )}

            {(item.location || item.city) && (
              <span
                className="agenda-location-badge is-location agenda-badge-venue"
                title={item.location || item.city}
              >
                <LuMapPin size={10} />
                <span>{item.location || item.city}</span>
              </span>
            )}
          </div>

          <span
            className={`agenda-pill-type ${
              isWebinar ? "pill-webinar" : "pill-clinical_session"
            }`}
          >
            {isWebinar ? (
              <LuVideo size={10} />
            ) : (
              <MdOutlineMedicalServices size={10} />
            )}
            <span>
              {isWebinar
                ? lang?.webinar || "Webinar"
                : "Session"}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};
